import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import type { EmpresaDatosAnio } from "@/@types/Api_Extracted";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/datatable";
import { CommonFeatures, EMPTY_ARRAY } from "@/hooks/useTanstackTable";
import { Paginator } from "@/components/ui/paginator";
import { createColumnHelper, tableFeatures, useTable } from "@tanstack/react-table";
import { convertToBest } from "@/lib/utils";

export function DatosAnioListTable() {
	const navigate = useNavigate();

	const api = useApi();
	const query = api.useQuery("get", "/v1/Empresa/DatosAnio", {});

	const handleNewInput = () => {
		void navigate("input/new");
	};

	const { columns, features } = useMemo(() => Columns(), []);
	const table = useTable({
		columns,
		features,
		data: query.data?.sort((a, b) => b.anio - a.anio) ?? EMPTY_ARRAY,
	});

	return (
		<>
			<DataTable
				table={table}
				noResultsMessage="Aún no hay datos registrados"
				errorMessage="Error al cargar los datos"
				isLoading={query.isPending}
				isError={query.isError}
			/>
			<div className="flex items-center justify-between mt-4">
				<div>
					{table.getPageCount() != 0 && (
						<Paginator
							pageCount={table.getPageCount()}
							pageIndex={table.state.pagination.pageIndex}
							shownPages={5}
							onPageChange={table.setPageIndex}
							canPrevious={table.getCanPreviousPage()}
							canNext={table.getCanNextPage()}
						/>
					)}
				</div>
				<Button variant="primary" onClick={handleNewInput}>
					Nuevo año
				</Button>
			</div>
		</>
	);
}

function Columns() {
	const columnHelper = createColumnHelper<typeof features, EmpresaDatosAnio>();

	const features = tableFeatures(CommonFeatures);

	const columns = columnHelper.columns([
		columnHelper.accessor("anio", {
			header: "Año",
			size: -1,
			cell: (props) => props.getValue(),
		}),
		columnHelper.accessor("cantidadTrabajadores", {
			header: "Trabajadores",
			size: -1,
			cell: (props) => props.getValue(),
		}),
		columnHelper.accessor("materiaPrima", {
			header: "Materia Prima",
			size: -1,
			cell: (props) => convertToBest(props.getValue(), "kg"),
		}),
		columnHelper.accessor("desechos", {
			header: "Desechos",
			size: -1,
			cell: (props) => convertToBest(props.getValue(), "kg"),
		}),
		columnHelper.accessor("facturacion", {
			header: "Facturación",
			size: -1,
			cell: (props) => convertToBest(props.getValue(), "€"),
		}),
		columnHelper.display({
			id: "acciones",
			header: "Acciones",
			size: -1,
			cell: (props) => <AccionesFila fila={props.row.original} />,
		}),
	]);

	return { columns, features };
}

function AccionesFila(props: { readonly fila: EmpresaDatosAnio; }) {
	const navigate = useNavigate();
	const handleEdit = () => {
		void navigate(`input/${props.fila.anio}`);
	};

	return (
		<div className="grid grid-cols-1 gap-2">
			<Button variant="secondary" onClick={handleEdit}>
				Editar
			</Button>
		</div>
	);
}
